import { useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGetEconomySettingsSharedCostsQuery } from '@client/api';
import { Button, Skeleton, Table, TableBody } from '@client/toolkit';

import { EconomyCardLoadError } from './economy-card-load-error.component';
import { EconomyCard } from './economy-card.component';
import { EconomyEditableCard } from './economy-editable-card.component';
import { EconomyTableHead } from './economy-table-head.component';
import { GroupAccountRow } from './group-account-row.component';
import { ECONOMY_HINT_CLASS } from '../economy-theme';
import {
  createEmptyGroupAccount,
  groupAccountsSchema,
  GroupAccountsFormValues as FormValues,
  toGroupAccountsFormValues,
} from '../portfolio-economy-settings.form';

/** Width hints; the standard table lays out on content, so these only steer it. */
const COLUMNS = [
  { key: 'account', className: 'w-[130px]' },
  { key: 'name', className: 'w-[280px]' },
  { key: 'classification', className: 'w-[150px]' },
  { key: 'amount', className: 'w-[180px]', align: 'right' as const },
  { key: 'method', className: 'w-[150px]' },
  { key: 'active', className: 'w-[110px]' },
  { key: 'actions', className: 'w-[150px]', align: 'right' as const },
] as const;

/** Reads the form the card owns, so it has to live inside its `FormProvider`. */
function GroupAccountsTable({ editing }: { editing: boolean }) {
  const { t } = useTranslation('economy');
  const { control, trigger, getFieldState, formState } = useFormContext<FormValues>();

  const { fields, remove, swap } = useFieldArray<FormValues, 'groupAccounts'>({
    control,
    name: 'groupAccounts',
  });

  /**
   * Removing or reordering rows shifts the existing errors but does not re-evaluate the
   * cross-row uniqueness test, so a resolved collision would stay red. Only re-runs when
   * the array already has an error.
   */
  const revalidateAfter = (mutate: () => void) => {
    const hadError = getFieldState('groupAccounts', formState).error != null;
    mutate();
    if (hadError) void trigger('groupAccounts');
  };

  return (
    <>
      {/* No `size`: the toolkit `Table` never puts it on `TableContext`, so the cells fall
          back to `medium` whatever is passed. Asking for `xs` here only reads as if the
          tables were compact. */}
      <Table className="w-full rounded-md">
        <EconomyTableHead section="settings.groupAccounts" columns={COLUMNS} />
        <TableBody>
          {fields.map((field, index) => (
            <GroupAccountRow
              key={field.id}
              index={index}
              editing={editing}
              isFirst={index === 0}
              isLast={index === fields.length - 1}
              onMoveUp={(from) => revalidateAfter(() => swap(from, from - 1))}
              onMoveDown={(from) => revalidateAfter(() => swap(from, from + 1))}
              onRemove={(at) => revalidateAfter(() => remove(at))}
            />
          ))}
        </TableBody>
      </Table>

      {fields.length === 0 && <p className="py-4 text-sm text-secondary">{t('settings.groupAccounts.empty')}</p>}

      {/* The uniqueness rule only means something while the konto numbers can be typed. */}
      {editing && <p className={`mt-3 px-3 ${ECONOMY_HINT_CLASS}`}>{t('settings.groupAccounts.uniqueHint')}</p>}
    </>
  );
}

/**
 * Sits in the card header beside Avbryt/Lagre, like the agreement cards' "Legg til".
 * Rendered inside the card's form, so it reaches the same field array as the table.
 */
function AddGroupAccountButton() {
  const { t } = useTranslation('economy');
  const { getValues, setValue } = useFormContext<FormValues>();

  // Writes through the form rather than a second `useFieldArray`: two instances of the same
  // array keep separate `fields` state, so appending on one leaves the table's copy behind.
  const append = (account: ReturnType<typeof createEmptyGroupAccount>) =>
    setValue('groupAccounts', [...getValues('groupAccounts'), account], { shouldDirty: true });

  return (
    <Button type="button" color="primary-dark" size="sm" onClick={() => append(createEmptyGroupAccount())}>
      {t('settings.groupAccounts.add')}
    </Button>
  );
}

/** Placeholder rows while the request is in flight — a count, not a claim about the real one. */
const SKELETON_ROW_COUNT = 3;

/**
 * Group-level cost accounts that get pushed down onto the buildings.
 *
 * The card fetches its own rows, as Konsern-nivå does — the surrounding page still seeds the
 * other cards from the mock.
 *
 * **Saving is still local.** `onSave` folds the values back into this card's own state, so the
 * read-only view shows what was just edited, but nothing is persisted until the update endpoint
 * exists (EXIO-2986). An edit is lost on reload.
 */
export function GroupAccountsCard() {
  const { t } = useTranslation('economy');

  const { data, isLoading, isError } = useGetEconomySettingsSharedCostsQuery();

  /**
   * What the card was last saved with, or `undefined` while it still shows the fetched rows.
   * Overlays the response rather than being seeded from it in an effect: seeding would mount
   * `EconomyEditableCard` with an empty array on the first render and only then reset it, and
   * `useForm` reads `defaultValues` at mount.
   */
  const [savedAccounts, setSavedAccounts] = useState<FormValues['groupAccounts']>();

  const defaultValues = useMemo(
    () => ({ groupAccounts: savedAccounts ?? (data == null ? [] : toGroupAccountsFormValues(data).groupAccounts) }),
    [savedAccounts, data],
  );

  if (isLoading) {
    return (
      <EconomyCard title={t('settings.groupAccounts.title')}>
        <div className="flex flex-col gap-3">
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
            <div key={index} data-testid="group-accounts-card-skeleton">
              <Skeleton variant="text" height="h-4" width="w-full" />
            </div>
          ))}
        </div>
      </EconomyCard>
    );
  }

  if (isError || data == null) {
    // No editable card here: there is nothing to edit, and a Rediger button over an empty table
    // would offer to save a list we failed to read.
    return (
      <EconomyCardLoadError
        title={t('settings.groupAccounts.title')}
        message={t('settings.groupAccounts.loadError')}
      />
    );
  }

  return (
    <EconomyEditableCard<FormValues>
      title={t('settings.groupAccounts.title')}
      defaultValues={defaultValues}
      schema={groupAccountsSchema}
      onSave={({ groupAccounts }) => setSavedAccounts(groupAccounts)}
      editActions={<AddGroupAccountButton />}
    >
      {(editing) => <GroupAccountsTable editing={editing} />}
    </EconomyEditableCard>
  );
}

export default GroupAccountsCard;
